// Mobil cihaz yetenekleri (Capacitor üzerinden)
// Web ortamında çağrılırsa sessizce no-op döner — tek kod base'de hem web hem mobil çalışır.
import { Capacitor } from '@capacitor/core';
import { Haptics, ImpactStyle, NotificationType } from '@capacitor/haptics';
import { PushNotifications } from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';

export const isNative = () => Capacitor.isNativePlatform();
export const platform = () => Capacitor.getPlatform();  // 'ios' | 'android' | 'web'

// -------------------- HAPTIC (Titreşim) --------------------
export async function hapticLight() {
  if (!isNative()) return;
  try { await Haptics.impact({ style: ImpactStyle.Light }); } catch { /* ignore */ }
}
export async function hapticMedium() {
  if (!isNative()) return;
  try { await Haptics.impact({ style: ImpactStyle.Medium }); } catch { /* ignore */ }
}
export async function hapticSuccess() {
  if (!isNative()) return;
  try { await Haptics.notification({ type: NotificationType.Success }); } catch { /* ignore */ }
}
export async function hapticWarning() {
  if (!isNative()) return;
  try { await Haptics.notification({ type: NotificationType.Warning }); } catch { /* ignore */ }
}
export async function hapticError() {
  if (!isNative()) return;
  try { await Haptics.notification({ type: NotificationType.Error }); } catch { /* ignore */ }
}

// -------------------- PUSH NOTIFICATIONS --------------------
// NOT: Bu fonksiyon SADECE google-services.json tanımlıysa (Firebase kurulu) çağrılmalı.
// Aksi halde Android/iOS native register() crash atar — try/catch bunu yakalayamaz.
// Firebase hazır olduğunda initPushNotifications'ı çağırmak için kullanıcıya özel bir ayar
// göstermelisiniz (örn. "Bildirimleri Aç" butonu). MVP: otomatik register devre dışı.
export async function initPushNotifications(apiBase, authToken, onReceive) {
  if (!isNative()) return;

  // Güvenlik: önce google-services.json var mı test et
  // Bunun en pratik yolu: Capacitor Preferences üzerinden bir "fcm_ready" bayrağı
  // Sırf register çağırmak sakıncalı; APK FCM olmadan derlenmişse crash eder.
  // Bu yüzden sadece explicit "FCM_ENABLED" flag varsa çalış.
  try {
    const { Preferences } = await import('@capacitor/preferences');
    const r = await Preferences.get({ key: 'fcm_enabled' });
    if (r.value !== 'true') {
      console.log('Push notifications: fcm_enabled bayrağı yok, register atlanıyor');
      return;
    }
  } catch { return; }

  try {
    const perm = await PushNotifications.checkPermissions();
    let status = perm.receive;
    if (status === 'prompt' || status === 'prompt-with-rationale') {
      const req = await PushNotifications.requestPermissions();
      status = req.receive;
    }
    if (status !== 'granted') {
      console.log('Push bildirim izni reddedildi');
      return;
    }

    await PushNotifications.register();

    PushNotifications.addListener('registration', async (token) => {
      try {
        await fetch(`${apiBase}/push/register-device`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
          body: JSON.stringify({
            token: token.value,
            platform: platform(),
            app: process.env.REACT_APP_MOBILE_APP || 'weddingtakip',
          }),
        });
      } catch (e) { console.error('Push register fail', e); }
    });

    PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error', err);
    });

    PushNotifications.addListener('pushNotificationReceived', (notif) => {
      hapticWarning();
      if (onReceive) onReceive(notif);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data || {};
      if (onReceive) onReceive(action.notification, data);
    });
  } catch (e) {
    console.error('Push init error', e);
  }
}

// Kullanıcı ayarlardan "Bildirimleri Aç" dediğinde çağrılır.
// Bu, FCM/google-services.json'un hazır olduğunu bildiğimiz durumda kullanılır.
export async function enablePushNotifications() {
  if (!isNative()) return { ok: false, reason: 'web' };
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: 'fcm_enabled', value: 'true' });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e.message };
  }
}

export async function disablePushNotifications() {
  if (!isNative()) return;
  try {
    const { Preferences } = await import('@capacitor/preferences');
    await Preferences.set({ key: 'fcm_enabled', value: 'false' });
  } catch { /* ignore */ }
}

// -------------------- LOCAL NOTIFICATIONS --------------------
// Push olmadan uygulama içinde test / anlık bildirim gösterme
export async function showLocalNotification({ title, body, id }) {
  if (!isNative()) return;
  try {
    await LocalNotifications.schedule({
      notifications: [{
        id: id || Math.floor(Math.random() * 1000000),
        title: title || 'Bildirim',
        body: body || '',
        schedule: { at: new Date(Date.now() + 100) },
      }],
    });
  } catch (e) { console.error('Local notif', e); }
}
